import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useClients } from '@/features/client-relationships';
import { fetchPendingInvitations } from '../api/invitationsApi';
import type { ClientOrInvitation } from '../types';

/**
 * React Query hook to fetch and merge both connected clients and pending invitations.
 * Uses the unified useClients hook from client-relationships feature.
 *
 * @returns Combined data with connected clients first, pending invitations last
 */
export function useClientsAndInvitations() {
  const clientsQuery = useClients();
  const invitationsQuery = useQuery({
    queryKey: ['advisor', 'invitations', 'pending'],
    queryFn: fetchPendingInvitations,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
  });

  // Merge data: connected clients first, pending invitations last
  const combinedData = useMemo(() => {
    const connected: ClientOrInvitation[] = (clientsQuery.data?.data || []).map(client => ({
      type: 'CONNECTED' as const,
      ...client,
    }));

    const pending: ClientOrInvitation[] = (invitationsQuery.data || []).map(inv => ({
      type: 'PENDING' as const,
      id: inv.id,
      name: inv.clientName || 'Unnamed Client',
      email: inv.clientEmail,
      invitedAt: inv.createdAt,
      canResend: inv.canResend,
      resendAvailableAt: inv.resendAvailableAt,
    }));

    return [...connected, ...pending];
  }, [clientsQuery.data, invitationsQuery.data]);

  return {
    data: combinedData,
    isLoading: clientsQuery.isLoading || invitationsQuery.isLoading,
    isError: clientsQuery.isError || invitationsQuery.isError,
  };
}

